import { useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import type { ReplayBundleV1 } from '@arrowise/shared';

interface ChatPanelProps {
    bundle: ReplayBundleV1;
    effectiveT: number;
    onSeekToTime: (t: number) => void;
}

export default function ChatPanel({ bundle, effectiveT, onSeekToTime }: ChatPanelProps) {
    const chatBottomRef = useRef<HTMLDivElement | null>(null);

    function formatMs(ms: number): string {
        const total = Math.floor(ms / 1000);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
    }

    // Markdown renderers similar to ChatPanel for consistent styling
    const markdownComponents = {
        pre: ({ children, ...props }: any) => (
            <pre className="rounded-md bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words border my-2 w-full" {...props}>{children}</pre>
        ),
        code: ({ inline, children, ...props }: any) => inline ? (
            <code className="bg-muted rounded px-1.5 py-0.5 text-xs font-mono border break-words" {...props}>{children}</code>
        ) : (
            <code className="block break-words whitespace-pre-wrap" {...props}>{children}</code>
        ),
        p: ({ children, ...props }: any) => (
            <p className="break-words overflow-wrap-anywhere leading-relaxed my-1" {...props}>{children}</p>
        ),
        a: ({ href, children, ...props }: any) => (
            <a href={href} target="_blank" rel="nofollow noopener noreferrer" className="underline text-primary hover:text-primary/80 break-all overflow-wrap-anywhere" {...props}>{children}</a>
        ),
        h1: ({ children, ...props }: any) => <h1 className="text-lg font-semibold mt-3 mb-2 break-words" {...props}>{children}</h1>,
        h2: ({ children, ...props }: any) => <h2 className="text-base font-semibold mt-3 mb-2 break-words" {...props}>{children}</h2>,
        h3: ({ children, ...props }: any) => <h3 className="text-sm font-semibold mt-2 mb-1 break-words" {...props}>{children}</h3>,
        ul: ({ children, ...props }: any) => <ul className="list-disc list-inside space-y-1 break-words my-2" {...props}>{children}</ul>,
        ol: ({ children, ...props }: any) => <ol className="list-decimal list-inside space-y-1 break-words my-2" {...props}>{children}</ol>,
        li: ({ children, ...props }: any) => <li className="break-words overflow-wrap-anywhere" {...props}>{children}</li>,
        blockquote: ({ children, ...props }: any) => (
            <blockquote className="border-l-4 border-muted-foreground/20 pl-3 italic break-words overflow-wrap-anywhere my-2" {...props}>{children}</blockquote>
        ),
        table: ({ children, ...props }: any) => (
            <div className="overflow-x-auto my-2 w-full"><table className="min-w-full border-collapse border border-muted-foreground/20 text-xs table-fixed" {...props}>{children}</table></div>
        ),
        th: ({ children, ...props }: any) => (
            <th className="border border-muted-foreground/20 px-2 py-1 bg-muted font-semibold text-left break-words" {...props}>{children}</th>
        ),
        td: ({ children, ...props }: any) => (
            <td className="border border-muted-foreground/20 px-2 py-1 break-words overflow-wrap-anywhere" {...props}>{children}</td>
        ),
        div: ({ children, ...props }: any) => (
            <div className="break-words overflow-wrap-anywhere w-full" {...props}>{children}</div>
        ),
    };

    // Auto-scroll chat viewport when visible interactions change count (avoid page scroll)
    useEffect(() => {
        const bottom = chatBottomRef.current;
        if (!bottom) return;
        const viewport = bottom.closest('[data-slot="scroll-area-viewport"]') as HTMLElement | null;
        if (viewport) {
            viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
        } else {
            // Fallback: keep it local if possible
            bottom.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, [bundle.ai.interactions.filter(m => m.t <= effectiveT).length]);

    return (
        <Card className="col-span-3 flex flex-col">
            <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="size-4" /> Chat</CardTitle>
                <CardDescription>AI interactions during the session</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
                <ScrollArea className="h-[58vh] pr-2">
                    <div className="flex flex-col gap-3">
                        {bundle.ai.interactions.length === 0 && (
                            <div className="text-sm text-muted-foreground px-2 py-1">No AI messages</div>
                        )}
                        {(bundle.ai.interactions.filter(m => m.t <= effectiveT)).map((m, idx) => (
                            <button key={idx} className="text-left rounded-md p-2 hover:bg-muted" onClick={() => onSeekToTime(m.t)}>
                                <div className="text-xs text-muted-foreground">{formatMs(m.t)}</div>
                                <div className="text-sm font-medium">Candidate</div>
                                <div className="text-sm whitespace-pre-wrap">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} components={markdownComponents}>
                                        {m.prompt}
                                    </ReactMarkdown>
                                </div>
                                <Separator className="my-2" />
                                <div className="text-sm font-medium">Assistant</div>
                                <div className="text-sm whitespace-pre-wrap">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} components={markdownComponents}>
                                        {m.response}
                                    </ReactMarkdown>
                                </div>
                            </button>
                        ))}
                        <div ref={chatBottomRef} />
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    );
}
